import { useQuery } from '@tanstack/react-query';
import { bulkApi } from '@/api/bulk';
import { queryKeys } from '@/lib/queryKeys';
import { useApiMutation, type MutationCallbacks } from '@/hooks/useApiMutation';
import type { BulkRunView, CreateBulkRunRequest } from '@/types/api';

export function useBulkRuns() {
  return useQuery({
    queryKey: queryKeys.bulk.all,
    queryFn: bulkApi.list,
    refetchInterval: (query) => (query.state.data?.some((run) => run.status === 'running') ? 5_000 : false),
  });
}

/** One run, asked again every few seconds while the worker is still moving it along. */
export function useBulkRun(id: string | null) {
  return useQuery({
    queryKey: queryKeys.bulk.run(id ?? ''),
    queryFn: () => bulkApi.get(id!),
    enabled: id !== null,
    refetchInterval: (query) => (query.state.data?.status === 'running' ? 3_000 : false),
  });
}

export function useCreateBulkRun(callbacks?: MutationCallbacks<BulkRunView, CreateBulkRunRequest>) {
  return useApiMutation(bulkApi.create, () => [queryKeys.bulk.all, queryKeys.sites.all, queryKeys.jobs.all], callbacks);
}

export function useStopBulkRun(id: string, callbacks?: MutationCallbacks<BulkRunView, void>) {
  return useApiMutation(() => bulkApi.stop(id), () => [queryKeys.bulk.all, queryKeys.bulk.run(id), queryKeys.jobs.all], callbacks);
}

export function useRetryBulkRun(id: string, callbacks?: MutationCallbacks<BulkRunView, void>) {
  return useApiMutation(() => bulkApi.retry(id), () => [queryKeys.bulk.all, queryKeys.bulk.run(id)], callbacks);
}
